import { useMemo, useState } from "react";
import type { Advisory, LiveTrackPoint, ReconFix } from "../data/liveApi";
import {
  arrow, changeOver, changeSincePrev, pressureDeltaColor, signed, spanLabel,
  toTrendPoints, trendFlags, utcMs, windDeltaColor,
  type Change, type TrendFlag, type TrendPoint,
} from "../engine/intensity";

// ── Intensity trend: wind AND pressure, update-to-update + 24 h ──
// The metric tiles read the working best track (what the header values come
// from); the advisory log reads NHC's public advisories, intermediates
// included — the official update-to-update record, where recon-driven
// pressure falls show up 3-hourly instead of 6-hourly.

// Saffir-Simpson from vmax in KNOTS
export function ssCategory(vmax: number): string {
  if (vmax >= 137) return "Cat 5";
  if (vmax >= 113) return "Cat 4";
  if (vmax >= 96) return "Cat 3";
  if (vmax >= 83) return "Cat 2";
  if (vmax >= 64) return "Cat 1";
  if (vmax >= 34) return "TS";
  return "TD";
}
// same scale as the map's track colours (Canvas catColor)
export function ssColor(vmax: number): string {
  if (vmax >= 137) return "#C084FC";
  if (vmax >= 113) return "#E74C3C";
  if (vmax >= 96) return "#E67E22";
  if (vmax >= 83) return "#F39C12";
  if (vmax >= 64) return "#F1C40F";
  if (vmax >= 34) return "#2ECC71";
  return "#95A5A6";
}

const fmtZ = (ms: number) => {
  const d = new Date(ms);
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${mon} ${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}${d.getUTCMinutes() ? `:${String(d.getUTCMinutes()).padStart(2, "0")}` : ""}Z`;
};

function DeltaLine({ v, unit, span, kind }: { v: number | null; unit: string; span: string; kind: "wind" | "pres" }) {
  if (v == null) return null;
  const color = v === 0 ? "rgba(255,255,255,0.55)" : kind === "wind" ? windDeltaColor(v) : pressureDeltaColor(v);
  return (
    <div className="delta-line" title={`${signed(v)} ${unit} over ${span}`}>
      <span className="d" style={{ color }}>{arrow(v)}</span>
      <span className="span">{span}</span>
    </div>
  );
}

export function FlagBanners({ flags }: { flags: TrendFlag[] }) {
  if (!flags.length) return null;
  return (
    <>
      {flags.map((f) => (
        <div
          key={f.code}
          className="flag-banner"
          style={{ background: `${f.color}2e`, border: `1px solid ${f.color}`, color: f.color }}
          title={f.code === "RD"
            ? "Rapid deepening: central pressure falling ≥42 mb in 24 h (Holliday & Thompson 1979)."
            : f.code === "RI"
              ? "Rapid intensification: max winds up ≥30 kt in 24 h (NHC's operational definition)."
              : "Rapid weakening: max winds down ≥30 kt in 24 h."}
        >
          ⚠ {f.label} <span style={{ fontWeight: 500, opacity: 0.85 }}>· {f.detail}</span>
        </div>
      ))}
    </>
  );
}

/** Category / Winds / Pressure tiles with update-to-update and 24 h deltas.
 *  `potential`: a Potential Tropical Cyclone — no category yet (its winds
 *  are a disturbance's), its fixes typed DB/LO. */
export function TrendMetrics({ track, potential = false }: { track: LiveTrackPoint[]; potential?: boolean }) {
  const pts = useMemo(
    () => toTrendPoints(track, (p) => ({ time: p.dt, vmax: p.vmax, pmin: p.pmin })),
    [track],
  );
  const last = track[track.length - 1];
  if (!last) return null;
  const prev = changeSincePrev(pts);
  const d24 = changeOver(pts, 24);
  const prevSpan = prev ? spanLabel(prev.hours) : "";
  return (
    <>
      <FlagBanners flags={trendFlags(d24)} />
      <div className="row" style={{ marginTop: 10 }}>
        <div className="metric">
          <div className="muted xs">Category</div>
          <div className="big" style={{ color: ssColor(last.vmax) }}>{potential ? "PTC" : ssCategory(last.vmax)}</div>
          {potential
            ? <div className="delta-line"><span className="span">not yet formed</span></div>
            : last.storm_type && <div className="delta-line"><span className="span">{last.storm_type}</span></div>}
        </div>
        <div className="metric">
          <div className="muted xs">Winds</div>
          <div className="big">{last.vmax} <span className="unit">kt</span></div>
          <DeltaLine v={prev?.dv ?? null} unit="kt" span={prevSpan} kind="wind" />
          <DeltaLine v={d24?.dv ?? null} unit="kt" span="24 h" kind="wind" />
        </div>
        <div className="metric">
          <div className="muted xs">Pressure</div>
          <div className="big">{last.pmin > 0 ? <>{last.pmin} <span className="unit">mb</span></> : "—"}</div>
          <DeltaLine v={prev?.dp ?? null} unit="mb" span={prevSpan} kind="pres" />
          <DeltaLine v={d24?.dp ?? null} unit="mb" span="24 h" kind="pres" />
        </div>
      </div>
    </>
  );
}

/** The official one-liner from the newest advisory in view. */
export function LatestAdvisoryLine({ adv }: { adv: Advisory | undefined }) {
  if (!adv) return null;
  const t = utcMs(adv.issued);
  return (
    <div className="muted xs adv-line">
      🏛️ NHC {adv.kind === "intermediate" ? "intermediate " : adv.kind === "special" ? "special " : ""}advisory{" "}
      {adv.url ? <a href={adv.url} target="_blank" rel="noreferrer">{adv.adv} ↗</a> : adv.adv}
      {t != null && (
        <> · {fmtZ(t)} <span style={{ opacity: 0.75 }}>({new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" })})</span></>
      )}
      {" — "}
      <b>{adv.vmax ?? "—"} kt</b>{adv.mph != null && <span> ({adv.mph} mph)</span>}
      {adv.pmin != null && <> · <b>{adv.pmin} mb</b></>}
    </div>
  );
}

// ── the log: a wind/pressure trace + one row per NHC update ──
interface LogRow {
  key: string;
  label: string; // "12A" or fix time
  intermediate: boolean;
  t: number;
  vmax: number | null;
  pmin: number | null;
  motion: string | null;
  url?: string;
}

function Sparkline({ pts, rows, recon = [] }: { pts: TrendPoint[]; rows: LogRow[]; recon?: { t: number; mslp: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 330, H = 118, PL = 26, PR = 30, PT = 8, PB = 16;
  if (pts.length < 2) return null;
  // aircraft fixes can be newer than the last advisory — widen the axis for them
  const t0 = pts[0].t;
  const t1 = Math.max(pts[pts.length - 1].t, ...recon.map((r) => r.t));
  const vs = pts.map((p) => p.vmax).filter((v): v is number => v != null);
  const ps = [...pts.map((p) => p.pmin).filter((v): v is number => v != null), ...recon.map((r) => r.mslp)];
  const vMax = Math.max(64, ...vs) + 10;
  const vMin = Math.max(0, Math.min(...vs) - 10);
  const pMin = Math.min(...ps) - 4, pMax = Math.max(...ps) + 4;
  const x = (t: number) => PL + ((t - t0) / Math.max(1, t1 - t0)) * (W - PL - PR);
  const yw = (v: number) => (H - PB) - ((v - vMin) / (vMax - vMin)) * (H - PB - PT);
  // pressure drawn INVERTED (low pressure up) so both lines climb with intensification
  const yp = (p: number) => PT + ((p - pMin) / Math.max(1, pMax - pMin)) * (H - PB - PT);
  const path = (sel: (p: TrendPoint) => number | null, y: (v: number) => number) => {
    let d = "", pen = false;
    for (const p of pts) {
      const v = sel(p);
      if (v == null) { pen = false; continue; }
      d += `${pen ? "L" : "M"}${x(p.t).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    }
    return d;
  };
  // shade the stretches flagged RI / rapid deepening (24 h window ending there)
  const bands: { a: number; b: number; color: string }[] = [];
  for (let i = 1; i < pts.length; i++) {
    const f = trendFlags(changeOver(pts, 24, i)).find((fl) => fl.code !== "RW");
    if (f) bands.push({ a: pts[i - 1].t, b: pts[i].t, color: f.color });
  }
  const SS = [{ v: 64, l: "H" }, { v: 96, l: "M" }, { v: 137, l: "5" }].filter((s) => s.v > vMin && s.v < vMax);
  const hi = hover != null ? rows.find((r) => r.t === pts[hover].t) : undefined;
  const legendY = H - PB - 4;

  return (
    <svg
      width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 6, display: "block" }}
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * W;
        let best = 0;
        for (let i = 1; i < pts.length; i++) if (Math.abs(x(pts[i].t) - px) < Math.abs(x(pts[best].t) - px)) best = i;
        setHover(best);
      }}
    >
      {bands.map((b, i) => (
        <rect key={i} x={x(b.a)} y={PT} width={Math.max(1, x(b.b) - x(b.a))} height={H - PB - PT} fill={b.color} opacity={0.1} />
      ))}
      {SS.map((s) => (
        <g key={s.v}>
          <line x1={PL} x2={W - PR} y1={yw(s.v)} y2={yw(s.v)} stroke="rgba(255,255,255,0.09)" strokeDasharray="3 4" />
          <text x={PL - 4} y={yw(s.v) + 3} fill="rgba(251,113,133,0.55)" fontSize="8" textAnchor="end">{s.l}</text>
        </g>
      ))}
      <text x={PL - 4} y={PT + 6} fill="rgba(251,113,133,0.8)" fontSize="8" textAnchor="end">{Math.round(vMax - 10)}</text>
      <text x={W - PR + 4} y={PT + 6} fill="rgba(56,189,248,0.85)" fontSize="8">{Math.round(pMin + 4)}</text>
      <text x={W - PR + 4} y={H - PB} fill="rgba(56,189,248,0.6)" fontSize="8">{Math.round(pMax - 4)}</text>
      <path d={path((p) => p.pmin, yp)} fill="none" stroke="#38BDF8" strokeWidth={1.8} />
      <path d={path((p) => p.vmax, yw)} fill="none" stroke="#FB7185" strokeWidth={1.8} />
      {/* hurricane-hunter center fixes: the MEASURED pressure behind the line */}
      {recon.filter((r) => r.t >= t0).map((r, i) => (
        <path
          key={i}
          d={`M${x(r.t).toFixed(1)},${(yp(r.mslp) - 3.2).toFixed(1)} l3.2,3.2 l-3.2,3.2 l-3.2,-3.2 Z`}
          fill="#E0F2FE" stroke="#0EA5E9" strokeWidth={0.8}
        />
      ))}
      {hover == null && (
        <text x={PL + 4} y={legendY} fontSize="8.5" fontWeight={600}>
          <tspan fill="#FB7185">━ wind (kt)</tspan>
          <tspan fill="#38BDF8" dx="8">━ pressure (mb, inverted)</tspan>
          {recon.length > 0 && <tspan fill="#E0F2FE" dx="8">◆ recon</tspan>}
        </text>
      )}
      <text x={PL} y={H - 3} fill="rgba(255,255,255,0.4)" fontSize="8">{fmtZ(t0)}</text>
      <text x={W - PR} y={H - 3} fill="rgba(255,255,255,0.4)" fontSize="8" textAnchor="end">{fmtZ(t1)}</text>
      {hover != null && (
        <g>
          <line x1={x(pts[hover].t)} x2={x(pts[hover].t)} y1={PT} y2={H - PB} stroke="rgba(255,255,255,0.35)" />
          {pts[hover].vmax != null && <circle cx={x(pts[hover].t)} cy={yw(pts[hover].vmax!)} r={3} fill="#FB7185" />}
          {pts[hover].pmin != null && <circle cx={x(pts[hover].t)} cy={yp(pts[hover].pmin!)} r={3} fill="#38BDF8" />}
          <text
            x={Math.min(Math.max(x(pts[hover].t), PL + 60), W - PR - 60)} y={PT + 8}
            fill="white" fontSize="9" fontWeight={600} textAnchor="middle"
          >
            {hi?.label ? `${hi.label} · ` : ""}{fmtZ(pts[hover].t)} · {pts[hover].vmax ?? "—"} kt · {pts[hover].pmin ?? "—"} mb
          </text>
        </g>
      )}
    </svg>
  );
}

export function AdvisoryLog({
  advisories, track, asOf, reconFixes,
}: { advisories: Advisory[]; track: LiveTrackPoint[]; asOf?: string | null; reconFixes?: ReconFix[] }) {
  const [expanded, setExpanded] = useState(false);

  const { rows, source } = useMemo(() => {
    // demo snapshots stop at a pre-impact advisory — don't leak later ones
    // (the advisory for an init goes out ~3 h after it)
    const cutoff = asOf ? (utcMs(asOf) ?? Infinity) + 3 * 3_600_000 : Infinity;
    const adv = advisories.filter((a) => (utcMs(a.issued) ?? Infinity) <= cutoff);
    if (adv.length >= 2) {
      return {
        source: "advisories" as const,
        rows: adv.map((a): LogRow => ({
          key: `${a.adv}-${a.issued}`, label: a.adv, intermediate: a.kind === "intermediate",
          t: utcMs(a.issued)!, vmax: a.vmax, pmin: a.pmin, url: a.url,
          motion: a.move_dir === "STNRY" ? "stnry" : a.move_dir ? `${a.move_dir} ${a.move_kt ?? ""}` : null,
        })),
      };
    }
    return {
      source: "fixes" as const,
      rows: track
        .filter((p) => p.dt)
        .map((p): LogRow => ({
          key: p.dt!, label: "", intermediate: false, t: utcMs(p.dt)!,
          vmax: p.vmax || null, pmin: p.pmin > 0 ? p.pmin : null, motion: null,
        })),
    };
  }, [advisories, track, asOf]);

  const pts = useMemo(
    () => rows.map((r) => ({ t: r.t, vmax: r.vmax, pmin: r.pmin })).sort((a, b) => a.t - b.t),
    [rows],
  );
  if (rows.length < 2) return null;

  const d24: Change | null = changeOver(pts, 24);
  const newestFirst = [...rows].sort((a, b) => b.t - a.t);
  const shown = expanded ? newestFirst : newestFirst.slice(0, 6);
  const idxOf = new Map(pts.map((p, i) => [p.t, i]));

  // Advisories are what NHC said AT THE TIME; the working best track (the
  // header tiles) gets revised as recon/satellite data are reanalysed — Polo's
  // 18Z advisory said 920 mb, recon found 892 mb for the 21Z one, and the
  // best track later moved 892 mb back to 18Z. Say so when the two disagree.
  const btPts = toTrendPoints(track, (p) => ({ time: p.dt, vmax: p.vmax, pmin: p.pmin }));
  const bt24 = source === "advisories" ? changeOver(btPts, 24) : null;
  const differs = !!(d24 && bt24 && (
    (d24.dv != null && bt24.dv != null && Math.abs(d24.dv - bt24.dv) >= 5) ||
    (d24.dp != null && bt24.dp != null && Math.abs(d24.dp - bt24.dp) >= 5)));

  return (
    <>
      <div className="muted xs" style={{ marginTop: 14 }}>
        📈 INTENSITY LOG · {source === "advisories" ? `${rows.length} NHC ADVISORIES` : `${rows.length} BEST-TRACK FIXES`}
      </div>
      <Sparkline
        pts={pts} rows={rows}
        recon={(reconFixes ?? []).flatMap((f) => (f.mslp != null ? [{ t: utcMs(f.t)!, mslp: f.mslp }] : []))}
      />
      {d24 && (d24.dv != null || d24.dp != null) && (
        <div className="muted sm" style={{ marginTop: 2 }}>
          Last 24 h{source === "advisories" ? " (as advised)" : ""}:{" "}
          {d24.dv != null && <b style={{ color: d24.dv === 0 ? undefined : windDeltaColor(d24.dv) }}>{signed(d24.dv)} kt</b>}
          {d24.dv != null && d24.dp != null && " · "}
          {d24.dp != null && <b style={{ color: d24.dp === 0 ? undefined : pressureDeltaColor(d24.dp) }}>{signed(d24.dp)} mb</b>}
        </div>
      )}
      {differs && bt24 && (
        <div className="muted xs" style={{ marginTop: 3, textTransform: "none", letterSpacing: 0, lineHeight: 1.45 }}>
          ℹ The header's best track says {bt24.dv != null ? `${signed(bt24.dv)} kt` : ""}
          {bt24.dv != null && bt24.dp != null ? " / " : ""}{bt24.dp != null ? `${signed(bt24.dp)} mb` : ""} over the same 24 h:
          advisories are NHC's real-time calls, while the best track is revised as recon and satellite data are reanalysed.
        </div>
      )}
      <table className="adv-table">
        <thead>
          <tr>
            <th>{source === "advisories" ? "ADV" : ""}</th>
            <th>VALID</th>
            <th className="num">WIND kt</th>
            <th className="num">PRES mb</th>
            {source === "advisories" && <th>MOTION</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => {
            const i = idxOf.get(r.t) ?? 0;
            const d = changeSincePrev(pts, i);
            const f = trendFlags(changeOver(pts, 24, i));
            return (
              <tr key={r.key} className={r.intermediate ? "inter" : undefined}>
                <td>
                  {r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.label}</a> : r.label}
                  {f.map((fl) => (
                    <span key={fl.code} className="mini-flag" style={{ color: fl.color, borderColor: fl.color }} title={fl.detail}>{fl.code}</span>
                  ))}
                </td>
                <td>{fmtZ(r.t)}</td>
                <td className="num">
                  {r.vmax ?? "—"}
                  {d?.dv != null && d.dv !== 0 && <span className="dd" style={{ color: windDeltaColor(d.dv) }}>{arrow(d.dv)}</span>}
                </td>
                <td className="num">
                  {r.pmin ?? "—"}
                  {d?.dp != null && d.dp !== 0 && <span className="dd" style={{ color: pressureDeltaColor(d.dp) }}>{arrow(d.dp)}</span>}
                </td>
                {source === "advisories" && <td className="muted">{r.motion ?? ""}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 6 && (
        <div className="muted xs center" style={{ marginTop: 4 }}>
          <span role="button" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setExpanded((v) => !v)}>
            {expanded ? "show latest 6" : `show all ${rows.length}`}
          </span>
        </div>
      )}
      <div className="muted xs" style={{ marginTop: 4, textTransform: "none", letterSpacing: 0 }}>
        {source === "advisories"
          ? "Δ vs the previous NHC advisory (intermediates in grey). Flags: RI +30 kt/24 h · RD −42 mb/24 h · RW −30 kt/24 h."
          : "No NHC advisories yet (invest or not yet named) — 6-hourly working best track shown."}
      </div>
    </>
  );
}

/** 24 h change line for an hourly replay frame (Explorer / focus). */
export function ReplayTrend({ frames, hour }: {
  frames: { dt: string | null; vmax: number; pmin: number }[];
  hour: number;
}) {
  const pts = useMemo(
    () => toTrendPoints(frames, (f) => ({ time: f.dt, vmax: f.vmax, pmin: f.pmin })),
    [frames],
  );
  const t = utcMs(frames[hour]?.dt);
  const idx = t == null ? -1 : pts.findIndex((p) => p.t === t);
  const d24 = idx > 0 ? changeOver(pts, 24, idx, 1) : null;
  if (!d24) return null;
  const flags = trendFlags(d24);
  return (
    <div className="muted sm" style={{ marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span>24 h change:</span>
      {d24.dv != null && <b style={{ color: d24.dv === 0 ? undefined : windDeltaColor(d24.dv) }}>{signed(Math.round(d24.dv))} kt</b>}
      {d24.dp != null && <b style={{ color: d24.dp === 0 ? undefined : pressureDeltaColor(d24.dp) }}>{signed(Math.round(d24.dp))} mb</b>}
      {flags.map((f) => (
        <span key={f.code} className="mini-flag" style={{ color: f.color, borderColor: f.color }} title={f.detail}>{f.label}</span>
      ))}
    </div>
  );
}
